import { FC, useState, useEffect, useRef } from "react";
import "./style.css";
import { message } from "antd";
import { Link } from "react-router-dom";
import { getArticles } from "@/services/Articles/getArticles";
import { motion } from "framer-motion";
import { Article } from "@/services/Articles/updateArticle";
import { createArticle } from "@/services/Articles/createArticle";
import { useTranslation } from "react-i18next";
import { Pagination, Input, Select, Skeleton, Empty, Button } from 'antd';
import { BlogGridItem } from "./BlogGridItem";
import { getSmartStorageItem } from "@/utils/smartStorage";

export const BlogGrid: FC = () => {
    const [messageApi, contextHolder] = message.useMessage();
    const [selectedCategory, setSelectedCategory] = useState<string>("Tümü");
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [articles, setArticles] = useState<Article[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const role = getSmartStorageItem("role");
    const refGridTitle = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();
    const pageSize = 12;

    useEffect(() => {
        const fetchArticles = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem("token") || undefined; // <-- token al
                const data = await getArticles(token); // token ile çağır
                setArticles(data);
            } catch (error) {
                messageApi.error("Makale verileri yüklenirken hata oluştu");
            } finally {
                setLoading(false);
            }
        };
        fetchArticles();
    }, []);


    const categories = ["Tümü", ...Array.from(new Set(articles.map(a => a.category)))];

    // 1. articles'i isPinned'e göre sırala
    const sortedArticles = [...articles].sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return 0;
    });

    // 2. filtreleme (kategori ve arama)
    const filteredPosts = sortedArticles.filter((article) => {
        const categoryMatch = selectedCategory === "Tümü" || article.category === selectedCategory;
        const searchMatch =
            article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            article.category.toLowerCase().includes(searchTerm.toLowerCase());
        return categoryMatch && searchMatch;
    });

    // 3. sayfalama
    const paginatedPosts = filteredPosts.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    const handleCreateArticle = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            messageApi.error("Admin token gerekli");
            return;
        }

        try {
            const newArticle = await createArticle(token);
            if (newArticle) {
                setArticles(prev => [newArticle, ...prev]);
                messageApi.success("Yeni makale eklendi!");
            } else {
                messageApi.error("Makale eklenirken hata oluştu");
            }
        } catch (err) {
            messageApi.error("Makale eklenirken hata oluştu");
            console.error(err);
        }
    };

    return (
        <div>
            {contextHolder}
            {
                role === "admin" && (
                    <Button
                        onClick={handleCreateArticle}
                        style={{
                            marginBottom: "20px",
                            padding: "5px 10px",
                            backgroundColor: "#4caf50",
                            border: "none",
                            borderRadius: "5px",
                            cursor: "pointer",
                            width: 'auto'
                        }}
                        type="primary"
                    >
                        YENİ MAKALE EKLE
                    </Button>
                )
            }
            <h1
                ref={refGridTitle}
                style={{ fontWeight: 'normal', marginBottom: '10px', color: "var(--color-text)" }}
            >{t("articles")}
            </h1>
            <div
                style={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "stretch",
                    padding: '10px 0px',
                    justifyContent: 'space-between'
                }}
            >
                <Select
                    defaultValue="Tümü"
                    styles={{

                        popup: {
                            root: {
                                width: 'fit-content',
                            },
                        },
                        root: {
                            width: '200px',
                        }
                    }}
                    options={categories.map(cat => ({ label: cat, value: cat }))}
                    onChange={(value) => {
                        setSelectedCategory(value);
                        setCurrentPage(1); // kategori değişince sayfayı başa al
                    }}
                />
                <Input
                    placeholder="Makale ara..."
                    suffix={<i className="fi fi-rr-search" style={{ color: 'grey', textAlign: 'center', transform: 'translateY(2px)' }}></i>}
                    style={{
                        borderRadius: 5,
                        width: 'fit-content',
                    }}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1); // arama yapıldığında sayfa 1'e reset
                    }}
                />
            </div>
            {!loading && paginatedPosts.length === 0 && (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Boş data"
                    style={{
                        width: '100%',
                        textAlign: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                />
            )}
            <div
                className="blog-grid"
            >
                {loading ? (
                    Array.from({ length: 6 }).map((_, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 30, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={{ delay: index * 0.1 }}
                        >
                            <div className="blog-item" style={{ display: 'flex', flexDirection: 'column' }}>
                                <Skeleton.Image
                                    active
                                    style={{ width: "100%", height: '170px', marginBottom: '10px', borderRadius: 5 }}
                                />
                                <Skeleton
                                    active
                                    title={{ width: "60%" }}
                                    paragraph={{ rows: 2 }}
                                />
                            </div>
                        </motion.div>
                    ))
                ) :
                    paginatedPosts.map((post, index) => (
                        <motion.div
                            key={`article-${post.id}`}
                            initial={{ opacity: 0, y: 30, scale: 0.95 }}
                            whileInView={{ opacity: 1, y: 0, scale: 1 }}
                            viewport={{ once: true, amount: 0.1 }}
                            transition={{ delay: index * 0.1 }}
                        >
                            <Link
                                to={`/blog/${post.slug}`}
                                style={{ textDecoration: "none", color: "inherit" }}>
                                <>
                                    {role === "admin" && (
                                        <p>{post.id}</p>
                                    )}
                                    <BlogGridItem {...post} />
                                </>
                            </Link>
                        </motion.div>
                    ))
                }
            </div>
            <Pagination
                align="end"
                style={{ display: filteredPosts.length === 0 ? 'none' : 'flex', marginTop: '10px' }}
                current={currentPage}
                pageSize={pageSize}
                total={filteredPosts.length}
                onChange={(page) => {
                    setCurrentPage(page);
                    if (refGridTitle.current) {
                        window.scrollTo({
                            top: refGridTitle.current.offsetTop - 20, // 20px boşluk bırak
                            behavior: 'smooth',
                        });
                    }
                }}
            />
        </div >
    );
};